import { useEffect, useReducer, useRef } from 'react'
import { useDocumentationDraft } from './useDocumentationDraft'
import type {
  DocumentationState,
  DocumentationAction,
} from '@/types/documentation'

const initialState: DocumentationState = {
  mode: 'pre-visit',
  recording: null,
  selectedNote: null,
  noteContent: '',
  noteApproval: { approved: false, approved_at: null, approved_by: null },
  addendums: [],
  diagnosisCodes: [],
  procedureCodes: [],
  suggestedDiagnosisCodes: [],
  suggestedProcedureCodes: [],
  prescriptions: [],
  treatmentPlan: {
    selected_diagnosis: null,
    diagnosis: '',
    medications: [],
    skincare: [],
    take_home_instructions: '',
    follow_up_in: '',
  },
  treatmentPlanApproved: false,
  followUpWeeks: 'none',
  followUpDate: null,
}

export function documentationReducer(
  state: DocumentationState,
  action: DocumentationAction,
): DocumentationState {
  switch (action.type) {
    case 'START_DOCUMENTATION':
      return { ...state, mode: 'documentation' }

    case 'ATTACH_RECORDING':
      return {
        ...state,
        recording: action.recording,
        selectedNote: action.note,
        noteContent: action.note?.content ?? '',
        noteApproval: { approved: false, approved_at: null, approved_by: null },
        addendums: [],
      }

    case 'DETACH_RECORDING':
      return {
        ...state,
        recording: null,
        selectedNote: null,
        noteContent: '',
        noteApproval: { approved: false, approved_at: null, approved_by: null },
        addendums: [],
      }

    case 'SELECT_NOTE':
      return {
        ...state,
        selectedNote: action.note,
        noteContent: action.note.content,
        noteApproval: { approved: false, approved_at: null, approved_by: null },
        addendums: [],
      }

    case 'UPDATE_NOTE_CONTENT':
      return { ...state, noteContent: action.content }

    case 'APPROVE_NOTE':
      return {
        ...state,
        noteApproval: {
          approved: true,
          approved_at: action.approvedAt,
          approved_by: action.approvedBy,
        },
      }

    case 'ADD_ADDENDUM':
      return {
        ...state,
        addendums: [
          ...state.addendums,
          {
            id: action.id,
            content: action.content,
            created_at: action.createdAt,
            author: action.author,
          },
        ],
      }

    case 'SET_DIAGNOSIS_CODES':
      return { ...state, diagnosisCodes: action.codes }

    case 'SET_PROCEDURE_CODES':
      return { ...state, procedureCodes: action.codes }

    case 'SET_SUGGESTED_CODES':
      return {
        ...state,
        suggestedDiagnosisCodes: action.suggestion.diagnosis_codes,
        suggestedProcedureCodes: action.suggestion.procedure_codes,
      }

    case 'ADD_PRESCRIPTION':
      return {
        ...state,
        prescriptions: [...state.prescriptions, action.prescription],
      }

    case 'UPDATE_PRESCRIPTION':
      return {
        ...state,
        prescriptions: state.prescriptions.map((p) =>
          p.id === action.prescription.id ? action.prescription : p,
        ),
      }

    case 'REMOVE_PRESCRIPTION':
      return {
        ...state,
        prescriptions: state.prescriptions.filter((p) => p.id !== action.id),
      }

    case 'SET_PRESCRIPTIONS':
      return {
        ...state,
        prescriptions: action.prescriptions,
      }

    case 'SET_TREATMENT_PLAN':
      return {
        ...state,
        treatmentPlan: action.plan,
        treatmentPlanApproved: false,
      }

    case 'APPROVE_TREATMENT_PLAN':
      return { ...state, treatmentPlanApproved: true }

    case 'SET_FOLLOW_UP':
      return {
        ...state,
        followUpWeeks: action.weeks,
        followUpDate: action.date ?? null,
      }

    default: {
      const _exhaustive: never = action
      return _exhaustive
    }
  }
}

export function useDocumentation(visitId?: string) {
  const { saveDraft, loadDraft, clearDraft } = useDocumentationDraft(visitId ?? '')

  const [state, dispatch] = useReducer(documentationReducer, initialState, () => {
    if (!visitId) return initialState
    const draft = loadDraft()
    if (!draft) return initialState
    // Discard stale drafts with old treatment plan shape
    if (!draft.treatmentPlan || !('take_home_instructions' in draft.treatmentPlan)) {
      draft.treatmentPlan = initialState.treatmentPlan
      draft.treatmentPlanApproved = false
    }
    return draft
  })

  // Debounced draft save when in documentation mode
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (!visitId || state.mode !== 'documentation') return

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      saveDraft(state)
    }, 500)

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    }
  }, [visitId, state, saveDraft])

  return { state, dispatch, clearDraft }
}
